import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Settings2, BarChart3, Plus, Minus, RotateCcw, Save, Trash2,
  RefreshCw, Dice5, AlertCircle, AlertTriangle,
} from "lucide-react";

const LEVELS = [1, 2, 3, 4, 5];
const ALLOWED_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

// Threshold above which an observed-vs-theoretical divergence is considered
// suspicious (possible RNG bias or misconfigured weights). Only enforced once
// the sample size is large enough to be statistically meaningful.
const BIAS_DELTA_THRESHOLD = 0.10;    // |observed - theoretical|
const BIAS_MIN_SAMPLES = 100;         // per-level spin count

// Returns the list of biased rows for a level: each entry is
// `{ letter, observed, theoretical, delta }`. Empty when the level has fewer
// than BIAS_MIN_SAMPLES spins or no letter exceeds the threshold.
function detectBias(levelData) {
  const total = levelData?.total || 0;
  if (total < BIAS_MIN_SAMPLES) return [];
  const observed = levelData.observed || {};
  const theoretical = levelData.theoretical || {};
  const letters = Array.from(new Set([
    ...Object.keys(theoretical),
    ...Object.keys(observed),
  ]));
  const flagged = [];
  letters.forEach((letter) => {
    const obs = (observed[letter] || 0) / total;
    const theo = theoretical[letter] || 0;
    const delta = obs - theo;
    if (Math.abs(delta) >= BIAS_DELTA_THRESHOLD) {
      flagged.push({ letter, observed: obs, theoretical: theo, delta });
    }
  });
  return flagged;
}

// Count weighted occurrences of each allowed letter in `arr`.
function tallyLetters(arr, allowedLetters = ALLOWED_LETTERS) {
  const out = {};
  allowedLetters.forEach((l) => { out[l] = 0; });
  (arr || []).forEach((l) => { if (out[l] !== undefined) out[l] += 1; });
  return out;
}

// Build the canonical list shape `[A, B, B, C]` from a weight map.
function tallyToList(tally) {
  const out = [];
  ALLOWED_LETTERS.forEach((l) => {
    for (let i = 0; i < (tally[l] || 0); i += 1) out.push(l);
  });
  return out;
}

function LevelEditor({ level, tally, onChange, isDirty, allowedLetters = ALLOWED_LETTERS }) {
  const total = allowedLetters.reduce((s, l) => s + (tally?.[l] || 0), 0);
  const adjust = (letter, delta) => {
    const next = { ...tally, [letter]: Math.max(0, (tally?.[letter] || 0) + delta) };
    onChange(next);
  };
  return (
    <Card
      className={`p-5 transition-shadow ${isDirty ? "ring-2 ring-amber-300 shadow-amber-100 shadow-lg" : ""}`}
      data-testid={`roulette-level-${level}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center text-white font-[Fredoka] font-bold">
            {level}
          </div>
          <div>
            <div className="font-[Fredoka] font-bold text-slate-900">Quadrícula {level}</div>
            <div className="text-xs text-slate-500">Total de bolas: <b>{total || "—"}</b></div>
          </div>
        </div>
        {isDirty && (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-300">Por guardar</Badge>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {allowedLetters.map((letter) => {
          const count = tally[letter] || 0;
          const prob = total > 0 ? (count / total) : 0;
          return (
            <div
              key={letter}
              className={`rounded-xl border-2 px-2 py-3 flex flex-col items-center gap-1 ${
                count > 0
                  ? "border-violet-300 bg-violet-50"
                  : "border-slate-200 bg-slate-50"
              }`}
              data-testid={`roulette-letter-${level}-${letter}`}
            >
              <div className="text-2xl font-[Fredoka] font-bold text-slate-900">{letter}</div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => adjust(letter, -1)}
                  disabled={count === 0}
                  className="w-7 h-7 rounded-lg bg-white border border-slate-300 grid place-items-center text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid={`roulette-dec-${level}-${letter}`}
                  aria-label={`Diminuir ${letter}`}
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <div className="w-7 text-center font-bold text-slate-900" data-testid={`roulette-count-${level}-${letter}`}>
                  {count}
                </div>
                <button
                  type="button"
                  onClick={() => adjust(letter, +1)}
                  className="w-7 h-7 rounded-lg bg-white border border-slate-300 grid place-items-center text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300 transition-colors"
                  data-testid={`roulette-inc-${level}-${letter}`}
                  aria-label={`Aumentar ${letter}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className={`text-[10px] tabular-nums ${count > 0 ? "text-violet-700 font-bold" : "text-slate-400"}`}>
                {count > 0 ? `${(prob * 100).toFixed(1)}%` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Visual proportion bar */}
      <div className="mt-4">
        <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
          {allowedLetters.filter((l) => (tally?.[l] || 0) > 0).map((letter, i) => {
            const w = total > 0 ? ((tally[letter] / total) * 100) : 0;
            const colors = ["#8b5cf6","#ec4899","#f97316","#eab308","#22c55e","#06b6d4","#3b82f6","#a855f7","#f43f5e","#10b981"];
            return (
              <div
                key={letter}
                className="grid place-items-center text-[9px] font-bold text-white"
                style={{ width: `${w}%`, backgroundColor: colors[i % colors.length] }}
                title={`${letter}: ${tally[letter]}/${total}`}
              >
                {w >= 6 ? letter : ""}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function ManageTab({ format, setFormat }) {
  const [serverLevels, setServerLevels] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [levelsForFormat, setLevelsForFormat] = useState(LEVELS);
  const [allowedLetters, setAllowedLetters] = useState(ALLOWED_LETTERS);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/roulette/config", { params: { format } });
      setLevelsForFormat(data.level_numbers || LEVELS);
      setAllowedLetters(data.allowed_letters || ALLOWED_LETTERS);
      const tallies = {};
      (data.level_numbers || LEVELS).forEach((l) => {
        tallies[l] = tallyLetters(data.levels[String(l)] || [], data.allowed_letters || ALLOWED_LETTERS);
      });
      setServerLevels(tallies);
      setDraft(tallies);
    } catch (e) {
      toast.error("Falha a carregar configuração da roleta");
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [format]);

  const dirty = useMemo(() => {
    if (!serverLevels || !draft) return {};
    const out = {};
    levelsForFormat.forEach((l) => {
      out[l] = allowedLetters.some((let_) => (serverLevels[l]?.[let_] || 0) !== (draft[l]?.[let_] || 0));
    });
    return out;
  }, [serverLevels, draft, levelsForFormat, allowedLetters]);
  const anyDirty = useMemo(() => Object.values(dirty).some(Boolean), [dirty]);

  const validation = useMemo(() => {
    if (!draft) return { ok: false, errors: [] };
    const errs = [];
    levelsForFormat.forEach((l) => {
      const total = allowedLetters.reduce((s, x) => s + (draft[l]?.[x] || 0), 0);
      if (total === 0) errs.push(`Quadrícula ${l}: vazia. Adiciona pelo menos uma letra.`);
    });
    return { ok: errs.length === 0, errors: errs };
  }, [draft, levelsForFormat, allowedLetters]);

  const save = async () => {
    if (!validation.ok) { toast.error(validation.errors[0]); return; }
    setSaving(true);
    try {
      const payload = { levels: {} };
      levelsForFormat.forEach((l) => { payload.levels[String(l)] = tallyToList(draft[l]); });
      await api.put("/admin/roulette/config", payload, { params: { format } });
      toast.success("Configuração da roleta guardada");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha a guardar";
      toast.error(typeof msg === "string" ? msg : "Falha a guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Restaurar as quadrículas para os valores predefinidos? Esta ação substitui a configuração atual.")) return;
    setResetting(true);
    try {
      await api.post("/admin/roulette/reset", null, { params: { format } });
      toast.success("Quadrículas restauradas para os valores predefinidos");
      await load();
    } catch (e) {
      toast.error("Falha a restaurar");
    } finally {
      setResetting(false);
    }
  };

  const revert = () => { if (serverLevels) setDraft(serverLevels); };

  if (!draft) {
    return <div className="text-slate-500 text-sm">A carregar…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 p-4 flex items-start gap-3">
        <Dice5 className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-700">
          <p className="font-bold text-slate-900 mb-1">Como funciona</p>
          <p>
            Cada quadrícula é uma tômbola: adicionas bolas (letras) e a probabilidade de cada
            letra ser sorteada é proporcional ao número de bolas dessa letra. Exemplo:
            <code className="mx-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded">A=1, B=1, C=2</code>
            → A=25%, B=25%, C=50%.
          </p>
        </div>
      </div>

      {!validation.ok && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 flex items-start gap-2" data-testid="roulette-validation-errors">
          <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
          <ul className="text-sm text-rose-800 space-y-0.5">
            {validation.errors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {levelsForFormat.map((l) => (
          <LevelEditor
            key={l}
            level={l}
            tally={draft[l]}
            onChange={(next) => setDraft((d) => ({ ...d, [l]: next }))}
            isDirty={!!dirty[l]}
            allowedLetters={allowedLetters}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 sticky bottom-4 z-10 bg-white/95 backdrop-blur rounded-2xl border border-slate-200 shadow-lg p-3">
        <Button
          onClick={save}
          disabled={!anyDirty || saving || !validation.ok}
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
          data-testid="roulette-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "A guardar…" : "Guardar alterações"}
        </Button>
        <Button
          variant="outline"
          onClick={revert}
          disabled={!anyDirty}
          data-testid="roulette-revert-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Desfazer
        </Button>
        <Button
          variant="outline"
          onClick={resetToDefaults}
          disabled={resetting}
          className="border-rose-300 text-rose-700 hover:bg-rose-50"
          data-testid="roulette-reset-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Restaurar predefinições
        </Button>
        {anyDirty && (
          <span className="text-xs text-amber-700 ml-2">⚠ Tens alterações por guardar</span>
        )}
      </div>
    </div>
  );
}

function StatsRow({ level, levelData }) {
  const config = levelData.config || [];
  const observed = levelData.observed || {};
  const theoretical = levelData.theoretical || {};
  const total = levelData.total || 0;
  const letters = Array.from(new Set([...Object.keys(theoretical), ...Object.keys(observed)])).sort();
  const biased = detectBias(levelData);
  const biasedSet = new Set(biased.map((b) => b.letter));

  return (
    <Card
      className={`p-5 ${biased.length > 0 ? "ring-2 ring-rose-300 shadow-rose-100 shadow-md" : ""}`}
      data-testid={`roulette-stats-level-${level}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 grid place-items-center text-white font-[Fredoka] font-bold">
            {level}
          </div>
          <div>
            <div className="font-[Fredoka] font-bold text-slate-900">Quadrícula {level}</div>
            <div className="text-xs text-slate-500">{total} sorteios registados</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {biased.length > 0 && (
            <Badge
              className="bg-rose-600 text-white border-0 inline-flex items-center gap-1"
              data-testid={`roulette-bias-badge-${level}`}
            >
              <AlertTriangle className="w-3 h-3" /> Enviesada
            </Badge>
          )}
          <div className="text-xs text-slate-400">Bolas: {config.length || "—"}</div>
        </div>
      </div>

      {biased.length > 0 && (
        <div
          className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
          data-testid={`roulette-bias-alert-${level}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
            <div>
              <p className="font-bold mb-0.5">
                Divergência acima de {Math.round(BIAS_DELTA_THRESHOLD * 100)}% detetada
              </p>
              <p className="text-xs leading-snug">
                Em {total.toLocaleString("pt-PT")} sorteios, {biased.length === 1 ? "a letra" : "as letras"}{" "}
                <b>{biased.map((b) => b.letter).join(", ")}</b>{" "}
                {biased.length === 1 ? "afasta-se" : "afastam-se"} significativamente da probabilidade teórica.
                Verifica a configuração ou investiga o gerador aleatório.
              </p>
            </div>
          </div>
        </div>
      )}

      {letters.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4 text-center">
          Sem sorteios registados nesta quadrícula.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid={`roulette-stats-table-${level}`}>
          <thead>
            <tr className="text-xs text-slate-500 border-b border-slate-200">
              <th className="text-left py-1.5">Letra</th>
              <th className="text-right py-1.5">Sorteios</th>
              <th className="text-right py-1.5">Observada</th>
              <th className="text-right py-1.5">Teórica</th>
              <th className="text-right py-1.5">Δ</th>
            </tr>
          </thead>
          <tbody>
            {letters.map((letter) => {
              const count = observed[letter] || 0;
              const obs = total > 0 ? (count / total) : 0;
              const theo = theoretical[letter] || 0;
              const delta = obs - theo;
              const isBiased = biasedSet.has(letter);
              const deltaColor =
                Math.abs(delta) < 0.03 ? "text-emerald-600" :
                Math.abs(delta) < 0.08 ? "text-amber-600" : "text-rose-600";
              return (
                <tr
                  key={letter}
                  className={`border-b border-slate-100 last:border-0 ${isBiased ? "bg-rose-50/60" : ""}`}
                >
                  <td className="py-1.5 font-bold text-slate-900 inline-flex items-center gap-1.5">
                    {letter}
                    {isBiased && <AlertTriangle className="w-3 h-3 text-rose-500" aria-label="Enviesada" />}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{count}</td>
                  <td className="py-1.5 text-right tabular-nums">{(obs * 100).toFixed(1)}%</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-500">{(theo * 100).toFixed(1)}%</td>
                  <td className={`py-1.5 text-right tabular-nums font-bold ${deltaColor}`}>
                    {delta >= 0 ? "+" : ""}{(delta * 100).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function StatsTab({ format }) {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/roulette/stats", { params: { days, format } });
      setStats(data);
    } catch (e) {
      toast.error("Falha a carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [days, format]);

  // Aggregate bias check across all levels: list which levels are flagged and
  // surface a single prominent banner at the top of the panel for fast triage
  // before tournament rounds.
  const biasReport = useMemo(() => {
    const out = [];
    if (!stats?.per_level) return out;
    const levels = Object.keys(stats.per_level).map(Number).sort((a, b) => a - b);
    levels.forEach((lvl) => {
      const ld = stats.per_level[String(lvl)] || {};
      const flagged = detectBias(ld);
      if (flagged.length > 0) {
        out.push({ level: lvl, total: ld.total || 0, letters: flagged });
      }
    });
    return out;
  }, [stats]);

  const wipe = async () => {
    if (!window.confirm("Apagar TODAS as estatísticas de sorteios? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete("/admin/roulette/stats");
      toast.success("Estatísticas apagadas");
      await load();
    } catch (e) {
      toast.error("Falha a apagar estatísticas");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                days === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`roulette-stats-range-${d}`}
            >
              {d === 365 ? "1 ano" : `${d} dias`}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="roulette-stats-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs text-slate-500">
            Total no período: <b className="text-slate-900">{stats?.total || 0}</b>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={wipe}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="roulette-stats-clear"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Apagar tudo
          </Button>
        </div>
      </div>

      {stats?.total === 0 && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          Ainda não há sorteios registados. Joga uma partida em qualquer modo para começar a recolher dados.
        </div>
      )}

      {biasReport.length > 0 && (
        <div
          className="rounded-2xl border-2 border-rose-400 bg-gradient-to-r from-rose-50 to-orange-50 p-4 shadow-md"
          data-testid="roulette-bias-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-600 grid place-items-center text-white shrink-0 shadow-sm">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-[Fredoka] font-bold text-rose-900 text-lg leading-tight">
                Roleta enviesada — atenção antes de torneios oficiais
              </h3>
              <p className="text-sm text-rose-800 mt-1">
                Divergência observada superior a <b>{Math.round(BIAS_DELTA_THRESHOLD * 100)}%</b>{" "}
                em {biasReport.length === 1 ? "1 quadrícula" : `${biasReport.length} quadrículas`}
                {" "}(com mais de {BIAS_MIN_SAMPLES} sorteios cada).
              </p>
              <ul className="mt-2 space-y-1 text-sm text-rose-900">
                {biasReport.map((b) => (
                  <li key={b.level} className="inline-flex flex-wrap items-center gap-1.5 mr-3" data-testid={`roulette-bias-summary-${b.level}`}>
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-rose-600 text-white text-xs font-bold">{b.level}</span>
                    <span className="font-bold">Quadrícula {b.level}</span>
                    <span className="text-rose-700">({b.total.toLocaleString("pt-PT")} sorteios):</span>
                    {b.letters.map((f) => (
                      <span
                        key={f.letter}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white border border-rose-300 text-rose-900 text-xs font-bold tabular-nums"
                        title={`Observada ${(f.observed * 100).toFixed(1)}% vs Teórica ${(f.theoretical * 100).toFixed(1)}%`}
                      >
                        {f.letter} {f.delta >= 0 ? "+" : ""}{(f.delta * 100).toFixed(1)}%
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-rose-700 mt-2 italic">
                Possíveis causas: configuração das quadrículas desatualizada, alteração recente das letras admitidas,
                ou enviesamento do gerador aleatório. Reverte temporariamente para os valores predefinidos no separador
                &quot;Gestão&quot; se for crítico.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.keys(stats?.per_level || {}).map(Number).sort((a, b) => a - b).map((l) => (
          <StatsRow key={l} level={l} levelData={(stats?.per_level || {})[String(l)] || { config: [], observed: {}, theoretical: {}, total: 0 }} />
        ))}
      </div>

      <div className="text-xs text-slate-500">
        <b>Δ</b> = diferença entre a probabilidade observada e a teórica. Verde &lt; 3%, âmbar &lt; 8%, vermelho ≥ 8%.
        Quanto mais sorteios, mais a observada deve aproximar-se da teórica.
      </div>
    </div>
  );
}

export default function AdminRoulette() {
  const [format, setFormat] = useState("original");
  return (
    <div className="max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 grid place-items-center text-white shadow-md">
            <Dice5 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-[Fredoka] font-bold text-slate-900" data-testid="roulette-page-title">Roleta</h1>
            <p className="text-sm text-slate-500">Configura letras e probabilidades de cada quadrícula e acompanha as estatísticas reais.</p>
          </div>
          {/* Format toggle — gestão e estatísticas mudam em conjunto. */}
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="roulette-format-toggle">
            {[
              { code: "original", label: "ORIGINAL", meta: "5×10" },
              { code: "quiz", label: "QUIZ", meta: "4×7" },
            ].map((opt) => {
              const active = format === opt.code;
              return (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setFormat(opt.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    active ? "bg-violet-600 text-white shadow" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`roulette-format-${opt.code}`}
                  title={`${opt.label} · ${opt.meta}`}
                >
                  {opt.label}
                  <span className={`ml-1 text-[10px] ${active ? "opacity-80" : "text-slate-400"}`}>{opt.meta}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <Tabs defaultValue="manage" data-testid="roulette-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="manage" data-testid="roulette-tab-manage">
            <Settings2 className="w-4 h-4 mr-2" /> Gestão
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="roulette-tab-stats">
            <BarChart3 className="w-4 h-4 mr-2" /> Estatísticas
          </TabsTrigger>
        </TabsList>
        <TabsContent value="manage">
          <ManageTab format={format} setFormat={setFormat} />
        </TabsContent>
        <TabsContent value="stats">
          <StatsTab format={format} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
